import type { Timer } from './types';

/** Verbleibende Zeit als mm:ss, bei langen Partien hh:mm:ss. */
export function zeitText(timer: Timer): string {
  if (timer.phase === 'EXPIRED') return 'ZEIT ABGELAUFEN';
  return zeitTextAusSekunden(timer.remainingSeconds);
}

/** Dieselbe Darstellung für alles, was eine Restzeit hat – auch für Abstimmungen. */
export function zeitTextAusSekunden(gesamt: number): string {
  const stunden = Math.floor(gesamt / 3600);
  const minuten = Math.floor((gesamt % 3600) / 60);
  const sekunden = gesamt % 60;
  const zwei = (n: number) => String(n).padStart(2, '0');
  return stunden > 0 ? `${stunden}:${zwei(minuten)}:${zwei(sekunden)}` : `${zwei(minuten)}:${zwei(sekunden)}`;
}

/**
 * Hintergrund eines Feldes aus den Farben der belegenden Teams.
 *
 * Ein Team füllt das Feld. Zwei teilen es diagonal. Drei und vier bekommen gleich große Bereiche.
 * Die Reihenfolge kommt vom Server und richtet sich nach der Teamliste, nicht nach dem Klick –
 * dadurch sitzt die Farbe eines Teams auf jedem Feld an derselben Stelle.
 */
export function feldHintergrund(farben: string[]): string | undefined {
  if (farben.length === 0) return undefined;
  if (farben.length === 1) return farben[0];
  if (farben.length === 2) {
    return `linear-gradient(135deg, ${farben[0]} 0 50%, ${farben[1]} 50% 100%)`;
  }
  const schritt = 360 / farben.length;
  const teile = farben.map((farbe, i) => `${farbe} ${i * schritt}deg ${(i + 1) * schritt}deg`);
  return `conic-gradient(from 45deg, ${teile.join(', ')})`;
}

/**
 * Schriftgröße eines Feldes, als Anteil der Feldbreite.
 *
 * Bewusst nicht in `rem`: Ein Feld ist in OBS so groß wie die Browserquelle und am Handy so groß
 * wie das Display. Eine feste Schriftgröße wäre in dem einen Fall verloren und im anderen zu groß.
 * Die tatsächliche Feldbreite misst das Board und reicht sie als CSS-Variable herein.
 *
 * Längere Titel werden kleiner gesetzt, damit sie nicht umbrechen müssen.
 */
export function titelGröße(titel: string): string {
  // Zwei Grenzen, die kleinere gewinnt:
  const nachLänge = titel.length > 22 ? 0.088 : titel.length > 14 ? 0.104 : 0.125;

  // … und das längste Wort. Zwischen Wörtern lässt sich umbrechen, mitten im Wort nicht – ein
  // langes Wort muss also in eine Zeile passen, sonst steht im Feld „danebengeschosse“.
  const längstesWort = Math.max(...titel.split(/\s+/).map((wort) => wort.length), 1);
  const nachWort = 1.55 / längstesWort;

  const anteil = Math.max(0.05, Math.min(nachLänge, nachWort));
  return `calc(var(--zelle, 6rem) * ${anteil.toFixed(3)})`;
}
